/* eslint-disable no-restricted-syntax */
import { RecurringEventRule } from "@iventis/domain-model/model/recurringEventRule";
import { RelativeEventRule } from "@iventis/domain-model/model/relativeEventRule";
import { compareAsc, startOfDay } from "date-fns";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { RelativeEventRuleRelationship } from "@iventis/domain-model/model/relativeEventRuleRelationship";
import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import { TimeRelativeTo } from "@iventis/domain-model/model/timeRelativeTo";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { Preposition } from "@iventis/types";
import { bothNull, hasObjectArrayChanged, hasSortableArrayChanged } from "@iventis/utilities";
import { TimeTypeDelegatorProps } from "./time-type-delegator";
import { EventBlockAndRelatedEventBlock, RunSheetRowNode } from "./run-sheet.types";

/** IMPORTANT: If you append to this, you must append to the switch statement in hasRelativeRuleChanged */
const emptyRelativeEventRule: RelativeEventRule = {
    id: null,
    offset: null,
    recurringEventRules: null,
    relationship: null,
    relativeTo: null,
    relativeToEventBlockTypeId: null,
    relativeToSpecificEventBlock: null,
    duration: null,
    endAt: null,
    specificTreeId: null,
    startAt: null,
};

/** IMPORTANT: If you append to this, you must append to the switch statement in hasRecurringRuleChanged */
const emptyRecurringEventRule: RecurringEventRule = {
    id: null,
    recurrenceType: null,
    recurringDates: null,
    recurringDaysInCycle: null,
    dayCycle: null,
    endOfRecurrence: null,
    endTime: null,
    startOfRecurrence: null,
    startTime: null,
};

/** Has the given relative event rule changed? */
export const hasRelativeRuleChanged = (oldRule: RelativeEventRule, newRule: RelativeEventRule, keysOfChangedValues?: (keyof RelativeEventRule)[]): boolean => {
    if ((oldRule == null && newRule != null) || (oldRule != null && newRule == null)) {
        return true;
    }
    let hasChanged = false;
    Object.keys(emptyRelativeEventRule).forEach((key: keyof RelativeEventRule) => {
        if (!bothNull(oldRule[key], newRule[key])) {
            switch (key) {
                case "id":
                case "offset":
                case "relationship":
                case "relativeTo":
                case "relativeToEventBlockTypeId":
                case "duration": {
                    // Value is primitive
                    if (oldRule[key] !== newRule[key]) {
                        keysOfChangedValues?.push(key);
                        hasChanged = true;
                    }
                    break;
                }
                case "relativeToSpecificEventBlock": {
                    // Value is an EventBlock
                    if (oldRule[key]?.id !== newRule[key]?.id) {
                        keysOfChangedValues?.push(key);
                        hasChanged = true;
                    }
                    break;
                }
                case "recurringEventRules": {
                    // Value is an array of RecurringEventRule's
                    if (hasObjectArrayChanged(oldRule[key], newRule[key], hasRecurringRuleChanged)) {
                        keysOfChangedValues?.push(key);
                        hasChanged = true;
                    }
                    break;
                }
                default:
                    break;
            }
        }
    });
    return hasChanged;
};

/** Has the given recurring event rule changed? */
export const hasRecurringRuleChanged = (oldRule: RecurringEventRule, newRule: RecurringEventRule): boolean => {
    if ((oldRule == null && newRule != null) || (oldRule != null && newRule == null)) {
        return true;
    }
    let hasChanged = false;
    Object.keys(emptyRecurringEventRule).forEach((key: keyof RecurringEventRule) => {
        if (!bothNull(oldRule[key], newRule[key])) {
            switch (key) {
                case "id":
                case "dayCycle":
                case "recurrenceType":
                case "startTime":
                case "endTime": {
                    // Value is primitive
                    if (oldRule[key] !== newRule[key]) {
                        hasChanged = true;
                    }
                    break;
                }
                case "endOfRecurrence":
                case "startOfRecurrence": {
                    // Value is a Date
                    if (compareAsc(oldRule[key], newRule[key]) !== 0) {
                        hasChanged = true;
                    }
                    break;
                }
                case "recurringDates": {
                    // Value is an array of dates
                    if (hasSortableArrayChanged(oldRule[key], newRule[key], (prev, next) => compareAsc(startOfDay(prev), startOfDay(next)))) {
                        hasChanged = true;
                    }
                    break;
                }
                case "recurringDaysInCycle": {
                    // Value is an array of numbers
                    if (hasSortableArrayChanged(oldRule[key], newRule[key], compareAsc)) {
                        hasChanged = true;
                    }
                    break;
                }
                default:
                    break;
            }
        }
    });
    return hasChanged;
};

export const relativeEventRuleKeysRequiringRefresh: (keyof RelativeEventRule)[] = ["relationship", "relativeToEventBlockTypeId", "relativeToSpecificEventBlock"];

/**
 * Searches the hierarchy for any relative rules which are related to the event block id passed in
 * @param hierarchy - hierarchy to be searched
 * @param eventBlockId - event block id you want to check
 * @param foundEventBlocks - array of event blocks which are related to the event block id passed into the function
 * @returns - an array of event blocks which are related to the event block id
 */
export function findDirectEventBlockDescendantsBySpecificRelativeEventRuleRelationship(
    hierarchy: Hierarchy<EventBlock>,
    eventBlockId: string,
    foundEventBlocks: EventBlock[] = []
) {
    // if event block id is null then early exit
    if (eventBlockId == null) {
        return [];
    }
    const searchedEventBlock = checkRelativeEventBlock(hierarchy.item, eventBlockId);
    if (searchedEventBlock != null) {
        foundEventBlocks.push(searchedEventBlock);
    }

    hierarchy.children.forEach((child) => findDirectEventBlockDescendantsBySpecificRelativeEventRuleRelationship(child, eventBlockId, foundEventBlocks));

    return foundEventBlocks;
}

function checkRelativeEventBlock(eventBlock: EventBlock, eventBlockId: string) {
    const { relativeEventRules } = eventBlock;
    if (relativeEventRules == null) {
        return null;
    }
    const relativeToSpecificEventBlockIds = relativeEventRules.map(({ relativeToSpecificEventBlock }) => relativeToSpecificEventBlock?.id);
    const eventBlockRelated = relativeToSpecificEventBlockIds.includes(eventBlockId);
    return eventBlockRelated ? eventBlock : null;
}

export function findAllEventBlockDescendantsBySpecificRelativeEventRuleRelationship(hierarchy: Hierarchy<EventBlock>, eventBlockId: string) {
    const foundEventBlocks: EventBlock[] = [];
    const findRelatedEventBlocks = (eventBlockHierarchy: Hierarchy<EventBlock>, ebId: string) => {
        const foundBlocks = findDirectEventBlockDescendantsBySpecificRelativeEventRuleRelationship(eventBlockHierarchy, ebId);
        foundBlocks.forEach((eventBlock) => {
            foundEventBlocks.push(eventBlock);
            findRelatedEventBlocks(eventBlockHierarchy, eventBlock.id);
        });
    };
    findRelatedEventBlocks(hierarchy, eventBlockId);
    return foundEventBlocks;
}

/** Generates the input related props for the TimeTypeDelegator component, based on the given event block type, and any additional overrides */
export const getTimeTypeDelegatorInputPropsFromEventBlockType = (
    type: ScheduleConfigEventBlockType,
    additions?: Partial<Pick<TimeTypeDelegatorProps, "disableInputs" | "hiddenInputs" | "initialFormValues" | "hiddenValuesBasedOnRelatedType">>
): Pick<TimeTypeDelegatorProps, "disableInputs" | "hiddenInputs" | "initialFormValues" | "hiddenValuesBasedOnRelatedType"> => {
    let props = {} as ReturnType<typeof getTimeTypeDelegatorInputPropsFromEventBlockType>;
    switch (type.calendarLevel) {
        // Activity
        case undefined:
        case null: {
            props = {
                disableInputs: { repeatActivity: () => true },
                hiddenInputs: { duration: () => true },
            };
            break;
        }
        // Event unit
        case 2: {
            props = {
                disableInputs: { preposition: () => true },
                hiddenInputs: {
                    relationship: () => true,
                    doesRepeatActivity: () => true,
                    repeatActivity: () => true,
                    specificRelatedToEvent: (values) => values.relatedType.calendarLevel === 1,
                },
                initialFormValues: { relationship: RelativeEventRuleRelationship.Specific, preposition: Preposition.After },
                hiddenValuesBasedOnRelatedType: {
                    2: { relativeTo: notAllowedTimeRelativeToByCalendarLevel(2) },
                },
            };
            break;
        }
        default:
            break;
    }
    props.disableInputs = { ...props.disableInputs, ...additions?.disableInputs };
    props.hiddenInputs = { ...props.hiddenInputs, ...additions?.hiddenInputs };
    props.initialFormValues = { ...props.initialFormValues, ...additions?.initialFormValues };
    props.hiddenValuesBasedOnRelatedType = { ...props.hiddenValuesBasedOnRelatedType, ...additions?.hiddenValuesBasedOnRelatedType };
    return props;
};

export const getDefaultRelativeToValue = (eventBlockType: ScheduleConfigEventBlockType) => {
    switch (eventBlockType.calendarLevel) {
        case 1:
            // Session
            return TimeRelativeTo.ZeroTime;
        case 2:
            // Event unit or schedule item
            return TimeRelativeTo.EndTime;
        default:
            throw new Error(`Event block type ${eventBlockType} does not have a default relative rule value`);
    }
};

export const notAllowedTimeRelativeToByCalendarLevel = (calendarLevel: number): TimeRelativeTo[] => {
    switch (calendarLevel) {
        // Event unit
        case 2:
            return [TimeRelativeTo.ZeroTime];
        default:
            return [];
    }
};

/** Takes a node and if it has relativeEventRules it adds it to an array with the related to event block Id */
export const addRelatedEventBlocksToArray = (node: RunSheetRowNode, relatedEventBlocks: EventBlockAndRelatedEventBlock[]) => {
    const { id, relativeEventRules } = node.data;
    // If there are relative rules
    if (relativeEventRules != null) {
        // Cycle through each rule
        relativeEventRules.forEach((rule) => {
            // If relativeToSpecificEventBlock is present
            if (rule.relativeToSpecificEventBlock != null) {
                // Add the eventBlockId and the relativeToSpecificEventBlock Id to an array
                relatedEventBlocks.push({ eventBlockId: id, relatedToEventBlockId: rule.relativeToSpecificEventBlock.id });
            }
        });
    }
};

/** Given a EventBlockAndRelatedEventBlock array it will find all of the relatedIds */
export const findEventBlocksRelatingToIdUsingMap = (relatedEventBlocks: EventBlockAndRelatedEventBlock[], id: string, relatedIds: string[] = []) => {
    // Find the directly related event block ids
    const relatingEventBlockIds = relatedEventBlocks.filter(({ relatedToEventBlockId }) => relatedToEventBlockId === id).map(({ eventBlockId }) => eventBlockId);
    // Add them to the relatedIds array
    relatedIds.push(...relatingEventBlockIds);
    // For each related Id we need to search to see if they have an relateEventBlocks
    relatingEventBlockIds.forEach((relatingEventBlockId) => findEventBlocksRelatingToIdUsingMap(relatedEventBlocks, relatingEventBlockId, relatedIds));
    return relatedIds;
};
